import { MediaImage } from '@/lib/mediaDisplay';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { useSites, useDeleteSite, useAccounts } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Settings, Trash2, Globe, Calendar, Database, ArrowRight, Users, Plus, Sparkles, Link2 } from 'lucide-react';
import { confirmDialog } from '@/lib/dialog';
import { resolveSiteAssetUrl } from '@/lib/siteAssets';
import { isMemberOf, useActiveAccount } from '@/lib/activeAccount';

export const Route = createFileRoute('/sites/')({
  component: SitesIndexPage,
});

function SitesIndexPage() {
  // Auth check is handled by parent route (sites.tsx)
  const { data, isLoading, error } = useSites();
  const { data: accountsData } = useAccounts();
  const deleteSite = useDeleteSite();

  // #527: sites from every account the user belongs to are merged into one
  // list, so multi-account users can't tell whose site is whose. Surface each
  // site's owning account + a client-side filter (the "switcher"). Single-account
  // users see none of this — no clutter, no behavior change.
  const accounts = accountsData?.data ?? [];
  const accountName = (pid?: string | null) =>
    accounts.find((a) => a.pid === pid)?.name;
  const multiAccount = accounts.length > 1;
  // AB#1014: the filter IS the account switch — an explicit pick from the
  // account menu lands here pre-filtered; "All accounts" clears the pick.
  const { pickedPid, setActive } = useActiveAccount();
  const accountFilter = pickedPid ?? 'all';
  const setAccountFilter = (v: string) => setActive(v === 'all' ? null : v);
  // AB#1017: new sites always land in the creator's own account, so inside an
  // account they're only a member of, point them home instead of offering Create.
  const inMemberAccount = isMemberOf(accounts, pickedPid);
  const ownAccount = accounts.find((a) => a.role === 'owner');

  const handleDelete = async (slug: string, name: string) => {
    if (await confirmDialog(`Delete site "${name}"? This cannot be undone.`, { destructive: true, confirmLabel: 'Delete site' })) {
      try {
        await deleteSite.mutateAsync(slug);
        toast.success('Site deleted');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete site');
      }
    }
  };

  // First-run: a brand-new account with no sites goes straight into the guided
  // wizard instead of an empty list.
  if (!isLoading && !error && data?.data && data.data.length === 0) {
    return <Navigate to="/sites/new" replace />;
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Sites</h1>
        <div className="flex items-center gap-3">
          {multiAccount && (
            <Select value={accountFilter} onValueChange={setAccountFilter}>
              <SelectTrigger size="sm" aria-label="Filter sites by account">
                <Users className="size-4" />
                <SelectValue>
                  {(v: unknown) =>
                    v === 'all' ? 'All accounts' : accountName(v as string) ?? 'Account'
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {accounts.map((a) => (
                  <SelectItem key={a.pid} value={a.pid}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!inMemberAccount && (
            <>
              <Button asChild variant="outline">
                <Link to="/sites/new" search={{ ai: 1, port: 1 }}>
                  <Link2 /> Bring a site over
                </Link>
              </Button>
              <Button asChild>
                <Link to="/sites/new">
                  <Plus /> <Sparkles /> New Site
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {inMemberAccount && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          <span>Sites you create go in your own account — switch to it to create one.</span>
          {ownAccount && (
            <Button size="sm" variant="outline" onClick={() => setActive(ownAccount.pid)}>
              Switch to {ownAccount.name}
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="rounded-xl border bg-card p-6">
              <Skeleton className="h-6 w-40 mb-4" />
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12 text-muted-foreground">Error loading sites</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data?.data
            ?.filter((site) => accountFilter === 'all' || site.account_pid === accountFilter)
            .map((site) => (
            <div
              key={site.pid}
              className="group relative flex flex-col rounded-xl border bg-card overflow-hidden transition-colors hover:border-primary/50"
            >
              {/* Main clickable area */}
              <Link
                to="/sites/$siteSlug/posts"
                params={{ siteSlug: site.slug }}
                className="block flex-1 p-6 pb-4"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="flex items-center gap-2 text-xl font-semibold truncate group-hover:text-primary transition-colors">
                      {(() => {
                        const favicon = resolveSiteAssetUrl(site.favicon_url, site.site_url);
                        return favicon ? (
                          <MediaImage scopeKey={site.slug} src={favicon} alt="" className="h-5 w-5 shrink-0 rounded" />
                        ) : null;
                      })()}
                      <span className="truncate">{site.name}</span>
                    </h3>
                    {site.domain && (
                      <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                        <Globe className="h-3.5 w-3.5" />
                        <span className="truncate">{site.domain}</span>
                      </div>
                    )}
                  </div>
                  <Badge variant="secondary" className="shrink-0 uppercase text-[10px] font-medium tracking-wider">
                    <Database className="h-3 w-3 mr-1" />
                    {site.db_type}
                  </Badge>
                </div>

                {/* Meta info */}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    Created {new Date(site.created_at).toLocaleDateString()}
                  </span>
                  {multiAccount && accountName(site.account_pid) && (
                    <span className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      {accountName(site.account_pid)}
                    </span>
                  )}
                </div>

                {/* Hover indicator */}
                <div className="absolute right-6 top-1/2 -translate-y-1/2 -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-[opacity,translate] ease-out motion-reduce:translate-x-0">
                  <ArrowRight className="h-5 w-5 text-primary" />
                </div>
              </Link>

              {/* Action bar */}
              <div className="flex items-center justify-between px-6 py-3 bg-muted/30 border-t border-border/50">
                <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-foreground" asChild>
                  <Link
                    to="/sites/$siteSlug/settings"
                    params={{ siteSlug: site.slug }}
                  >
                    <Settings className="h-4 w-4 mr-1.5" />
                    Settings
                  </Link>
                </Button>
                {/* AB#1016: deleting a site is owner-only. */}
                {!isMemberOf(accounts, site.account_pid) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleDelete(site.slug, site.name)}
                    disabled={deleteSite.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
